import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { getLoginRouteForRole, ROUTES } from "@/constants/routes";
import { API_ROUTES, type AuthScope, getScopedAuthApiBase } from "@/constants/apiRoutes";
import { clearAccessToken, getAccessToken, setAccessToken } from "@/shared/utils/token";
import { storage } from "@/shared/utils/storage";
import { getPortalHost, getPortalLoginHref } from "@/utils/portalHost";

export type ApiRequestConfig = AxiosRequestConfig;

export type ApiError = Error & {
  status?: number;
  data?: unknown;
};

const AUTH_STORAGE_KEY = "confidacare_auth";
const API_BASE_PATH = "/api";
const refreshRequests = new Map<AuthScope, Promise<string | null>>();

export function normalizeApiUrl(url: string) {
  if (/^https?:\/\//i.test(url)) {
    try {
      const parsed = new URL(url);
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    } catch {
      return url;
    }
  }

  if (url === API_BASE_PATH) {
    return "/";
  }

  if (url.startsWith(`${API_BASE_PATH}/`)) {
    return url.slice(API_BASE_PATH.length);
  }

  return url;
}

function redirectToLogin() {
  if (typeof window === "undefined") {
    return;
  }

  const currentAuth = storage.get<{ scope?: AuthScope; user?: { role?: string } }>(AUTH_STORAGE_KEY);
  const portal = getPortalHost();
  if (portal === "admin") {
    clearAccessToken();
    storage.remove(AUTH_STORAGE_KEY);
    const adminLoginHref = getPortalLoginHref("admin");
    if (window.location.href !== adminLoginHref) {
      window.location.assign(adminLoginHref);
    }
    return;
  }
  if (portal === "provider") {
    clearAccessToken();
    storage.remove(AUTH_STORAGE_KEY);
    const providerLoginHref = getPortalLoginHref("provider");
    if (window.location.href !== providerLoginHref) {
      window.location.assign(providerLoginHref);
    }
    return;
  }
  const roleFromScope =
    currentAuth?.scope === "admins"
      ? "admin"
      : currentAuth?.scope === "providers"
        ? "provider"
        : undefined;
  const fallbackRole = portal === "admin"
    ? "admin"
    : portal === "provider"
      ? "provider"
      : window.location.pathname.startsWith(ROUTES.adminsHome)
        ? "admin"
        : window.location.pathname.startsWith(ROUTES.providersHome)
          ? "provider"
          : undefined;
  const resolvedRole = currentAuth?.user?.role ?? roleFromScope ?? fallbackRole;
  const loginRoute = getLoginRouteForRole(resolvedRole);
  const loginHref =
    resolvedRole === "admin"
      ? getPortalLoginHref("admin")
      : resolvedRole === "provider" || resolvedRole === "staff"
        ? getPortalLoginHref("provider")
        : getPortalLoginHref("patient");

  clearAccessToken();
  storage.remove(AUTH_STORAGE_KEY);

  if (window.location.pathname !== loginRoute) {
    window.location.assign(loginHref);
  }
}

function toApiError(error: unknown): ApiError {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;
    const message =
      responseData &&
      typeof responseData === "object" &&
      "message" in responseData
        ? String(responseData.message)
        : error.message;

    const apiError = new Error(message) as ApiError;
    apiError.status = error.response?.status;
    apiError.data = responseData;
    return apiError;
  }

  return error instanceof Error ? (error as ApiError) : new Error("Request failed");
}

function getCurrentAuthScope(): AuthScope {
  const portal = getPortalHost();
  if (portal === "admin") return "admins";
  if (portal === "provider") return "providers";

  const currentAuth = storage.get<{ scope?: AuthScope; user?: { role?: string } }>(AUTH_STORAGE_KEY);
  if (currentAuth?.scope) return currentAuth.scope;
  if (currentAuth?.user?.role === "admin") return "admins";
  if (currentAuth?.user?.role === "provider" || currentAuth?.user?.role === "staff") return "providers";
  return "user";
}

function getRefreshRoute(scope: AuthScope) {
  return `${getScopedAuthApiBase(scope)}/refresh`;
}

async function refreshAccessTokenOnce(scope: AuthScope) {
  const inFlight = refreshRequests.get(scope);
  if (inFlight) {
    return inFlight;
  }

  const request = (async () => {
      try {
        const response = await axios.post<{ accessToken?: string }>(
          normalizeApiUrl(getRefreshRoute(scope)),
          undefined,
          {
            baseURL: API_BASE_PATH,
            withCredentials: true,
          },
        );
        const nextToken = response.data.accessToken ?? null;
        if (nextToken) {
          setAccessToken(nextToken);
          const saved = storage.get<Record<string, unknown>>(AUTH_STORAGE_KEY);
          if (saved && scope !== "user") {
            storage.set(AUTH_STORAGE_KEY, {
              ...saved,
              token: nextToken,
            });
          }
        }
        return nextToken;
      } catch {
        return null;
      } finally {
        refreshRequests.delete(scope);
      }
    })();

  refreshRequests.set(scope, request);
  return request;
}

const instance = axios.create({
  baseURL: API_BASE_PATH,
  withCredentials: true,
  timeout: 15000,
});

instance.interceptors.request.use((config) => {
  if (config.url) {
    config.url = normalizeApiUrl(config.url);
  }

  const token = getAccessToken();

  const hasAuthorizationHeader =
    Boolean(config.headers?.Authorization) ||
    Boolean((config.headers as Record<string, string | undefined>)?.authorization);

  if (token && !hasAuthorizationHeader) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const apiError = toApiError(error);
    const message = apiError.message.toLowerCase();
    const originalConfig = error.config as (InternalAxiosRequestConfig & { _retry?: boolean }) | undefined;
    const requestUrl = normalizeApiUrl(originalConfig?.url ?? "");
    const scope = getCurrentAuthScope();
    const isRefreshCall =
      requestUrl.includes(normalizeApiUrl(API_ROUTES.auth.refresh)) ||
      requestUrl.includes(normalizeApiUrl(API_ROUTES.admins.auth.refresh)) ||
      requestUrl.includes(normalizeApiUrl(API_ROUTES.providerPortal.auth.refresh));
    const hasAuthError =
      apiError.status === 401 &&
      (message.includes("invalid or expired token") ||
        message.includes("expired token") ||
        message.includes("unauthorized"));

    if (
      hasAuthError &&
      !isRefreshCall &&
      originalConfig &&
      !originalConfig._retry
    ) {
      originalConfig._retry = true;
      const refreshedToken = await refreshAccessTokenOnce(scope);
      if (refreshedToken) {
        originalConfig.headers = originalConfig.headers ?? {};
        originalConfig.headers.Authorization = `Bearer ${refreshedToken}`;
        return instance.request(originalConfig);
      }
    }

    if (hasAuthError) {
      redirectToLogin();
    }

    return Promise.reject(apiError);
  },
);

async function unwrap<T>(request: Promise<AxiosResponse<T>>) {
  const response = await request;
  return response.data;
}

export const axiosInstance: AxiosInstance = instance;

export const axiosClient = {
  get: <T>(url: string, config?: ApiRequestConfig) =>
    unwrap<T>(instance.get<T>(url, config)),
  post: <T, B = unknown>(url: string, data?: B, config?: ApiRequestConfig) =>
    unwrap<T>(instance.post<T>(url, data, config)),
  put: <T, B = unknown>(url: string, data?: B, config?: ApiRequestConfig) =>
    unwrap<T>(instance.put<T>(url, data, config)),
  patch: <T, B = unknown>(url: string, data?: B, config?: ApiRequestConfig) =>
    unwrap<T>(instance.patch<T>(url, data, config)),
  delete: <T>(url: string, config?: ApiRequestConfig) =>
    unwrap<T>(instance.delete<T>(url, config)),
};

